import { Toast } from '@byecode/ui/components/Toast'
import { nanoid } from '@lighthouse/tools'
import type { UploadOptions } from '@rpldy/uploady'
import type { Fragment, Node as PMNode } from '@tiptap/pm/model'
import { DOMParser as PM_DOMParser, Fragment as PM_Fragment, Slice } from '@tiptap/pm/model'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import type { EditorView } from '@tiptap/pm/view'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import { t } from 'i18next'

import { CDN_IMAGE_PREFIX } from '../../../utils'
import { tokenStorage } from '../../../utils/token'
import type { TokenRefreshManager } from '../../../utils/tokenRefreshManager'
import { createUploadPlaceholderWidget } from '../createUploadPlaceholderWidget'

interface ParseVideoPluginParams {
    appId: string
    uploadOptions?: UploadOptions
    tokenRefreshManager?: TokenRefreshManager
    /** 最大上传大小（字节） */
    maxSize?: number
}

interface UploadingItem {
    id: string
    file: File | string
}

type PlaceholderMeta =
    | { type: 'add'; id: string; pos: number }
    | { type: 'addMany'; items: Array<{ id: string; pos: number }> }
    | { type: 'remove'; id: string }

interface SliceSlot {
    offset: number
    file: string | File
}

async function webVideo2File(videoUrl: string): Promise<File> {
    const blob = await fetch(videoUrl).then(res => res.blob())
    const ext = blob.type.split('/')[1] || 'mp4'
    return new File([blob], `${nanoid(10)}.${ext}`, { type: blob.type })
}

export const UPLOAD_VIDEO_PLUGIN_KEY = new PluginKey<DecorationSet>('upload-video')
const DEFAULT_MAX_SIZE = 100 * 1024 * 1024 // 100MB for video

function formatFileSize(bytes: number): string {
    if (bytes >= 1024 * 1024 * 1024) {
        return `${(bytes / (1024 * 1024 * 1024)).toFixed(0)}GB`
    }
    if (bytes >= 1024 * 1024) {
        return `${(bytes / (1024 * 1024)).toFixed(0)}MB`
    }
    if (bytes >= 1024) {
        return `${(bytes / 1024).toFixed(0)}KB`
    }
    return `${bytes}B`
}

function isPlaceholderMeta(meta: unknown): meta is PlaceholderMeta {
    return meta !== null && typeof meta === 'object' && 'type' in meta
}

function needUploadVideo(node: PMNode): boolean {
    return node.type.name === 'video' && !node.attrs.src?.startsWith(CDN_IMAGE_PREFIX)
}

function getVideoFile(node: PMNode): string {
    return node.attrs.src ?? ''
}

/**
 * 从 slice 中移除需上传的视频，返回新 slice 及每个视频的槽位 offset（用于 Decoration 位置）
 */
function processSliceRemoveVideos(slice: Slice): { newSlice: Slice; slots: SliceSlot[] } {
    const slots: SliceSlot[] = []

    function processFragment(frag: Fragment, baseOffset: number): { newFrag: PM_Fragment; nextOffset: number } {
        const nodes: PMNode[] = []
        let offset = baseOffset
        frag.forEach((node: PMNode) => {
            if (needUploadVideo(node)) {
                slots.push({ offset, file: getVideoFile(node) })
                return
            }
            if (node.content.size > 0) {
                const { newFrag } = processFragment(node.content, offset + 1)
                const newNode = node.copy(newFrag)
                nodes.push(newNode)
                offset += newNode.nodeSize
            } else {
                nodes.push(node)
                offset += node.nodeSize
            }
        })
        return { newFrag: PM_Fragment.fromArray(nodes), nextOffset: offset }
    }

    const { newFrag } = processFragment(slice.content, 0)
    return {
        newSlice: new Slice(newFrag, slice.openStart, slice.openEnd),
        slots
    }
}

class UploadVideoManager {
    readonly #uploadOptions?: UploadOptions

    readonly #tokenRefreshManager?: TokenRefreshManager

    readonly #maxSize: number

    readonly #pluginKey: PluginKey<DecorationSet>

    constructor(
        uploadOptions: UploadOptions | undefined,
        tokenRefreshManager: TokenRefreshManager | undefined,
        maxSize: number,
        pluginKey: PluginKey<DecorationSet>
    ) {
        this.#uploadOptions = uploadOptions
        this.#tokenRefreshManager = tokenRefreshManager
        this.#maxSize = maxSize
        this.#pluginKey = pluginKey
    }

    findPlaceholder(view: EditorView, id: string): number {
        const decoSet = this.#pluginKey.getState(view.state)
        if (!decoSet) return -1
        const match = decoSet.find(undefined, undefined, (spec: { id?: string }) => spec.id === id)
        return match.length ? match[0].from : -1
    }

    removePlaceholder(view: EditorView, id: string) {
        const { tr } = view.state
        tr.setMeta(this.#pluginKey, { type: 'remove', id })
        view.dispatch(tr)
    }

    async upload(view: EditorView, item: UploadingItem, savedPos?: number, isRetry = false) {
        const { destination } = this.#uploadOptions ?? {}

        if (!destination || !destination.url) {
            return
        }

        const file = item.file instanceof File ? item.file : await webVideo2File(item.file)
        let pos = this.findPlaceholder(view, item.id)

        if (pos === -1) {
            if (isRetry && savedPos !== undefined) {
                pos = savedPos
            } else {
                return
            }
        }

        if (file.size > this.#maxSize) {
            Toast.error(`${t('fileExceedsUploadLimit', { v: formatFileSize(this.#maxSize) })}`)
            this.removePlaceholder(view, item.id)
            return
        }

        const formData = new FormData()
        formData.append(destination.filesParamName ?? 'file', file)
        if (destination.params) {
            Object.entries(destination.params).forEach(([key, value]) => {
                if (typeof value === 'string') {
                    formData.append(key, value)
                }
            })
        }

        const headers: Record<string, string> = {}
        if (destination.headers) {
            Object.entries(destination.headers as Record<string, string>).forEach(([key, value]) => {
                if (key.toLowerCase() !== 'content-type') {
                    headers[key] = value
                }
            })
        }

        const token = tokenStorage.getAccessToken()
        if (token) {
            headers['Authorization'] = `Bearer ${token}`
        }

        try {
            const res = await fetch(destination.url, { method: 'POST', body: formData, headers })

            if (res.status === 401) {
                if (isRetry) {
                    this.removePlaceholder(view, item.id)
                    throw new Error('重试后仍然收到 401 错误')
                }

                if (!this.#tokenRefreshManager) {
                    this.removePlaceholder(view, item.id)
                    return
                }

                try {
                    const currentPos = pos
                    await this.#tokenRefreshManager.handleUpload401(item.id, async () => {
                        await this.upload(view, item, currentPos, true)
                    })
                } catch {
                    this.removePlaceholder(view, item.id)
                }

                return
            }

            const data = await res.json()
            const { url } = data.content

            const videoNode = view.state.schema.nodes.video.create({ src: url })
            const { tr, doc } = view.state

            const currentPlaceholderPos = this.findPlaceholder(view, item.id)
            if (currentPlaceholderPos === -1) {
                const insertPos = Math.min(pos, doc.content.size)
                tr.insert(insertPos, videoNode)
            } else {
                tr.insert(currentPlaceholderPos, videoNode)
                tr.setMeta(this.#pluginKey, { type: 'remove', id: item.id })
            }
            view.dispatch(tr)
        } catch (error) {
            this.removePlaceholder(view, item.id)
            if (isRetry) {
                throw error
            }
        }
    }

    /** 使用 Decoration 添加占位符并开始上传（drop、paste 文件）- 不修改 document */
    createDecorationPlaceholdersAndUpload(view: EditorView, files: (File | string)[], pos: number) {
        const items: UploadingItem[] = files.map(file => ({
            id: nanoid(),
            file
        }))
        const addMany: PlaceholderMeta = {
            type: 'addMany',
            items: items.map(item => ({ id: item.id, pos }))
        }
        const { tr } = view.state
        tr.setMeta(this.#pluginKey, addMany)
        view.dispatch(tr)
        items.forEach(item => this.upload(view, item))
    }

    /**
     * HTML 粘贴：插入无视频 slice，在 slot 位置加 Decoration 并开始上传，上传完成后插入 video 节点并移除 Decoration
     */
    pasteHtmlWithDecorationPlaceholders(view: EditorView, newSlice: Slice, slots: SliceSlot[], insertFrom: number) {
        if (slots.length === 0) return
        const items: UploadingItem[] = slots.map(slot => ({ id: nanoid(), file: slot.file }))
        const addMany: PlaceholderMeta = {
            type: 'addMany',
            items: items.map((item, i) => ({ id: item.id, pos: insertFrom + slots[i].offset }))
        }
        const tr = view.state.tr.replaceSelection(newSlice)
        tr.setMeta(this.#pluginKey, addMany)
        view.dispatch(tr)
        items.forEach(item => this.upload(view, item))
    }
}

// 处理视频上传逻辑
export const ParseVideoPlugin = ({ uploadOptions, appId, tokenRefreshManager, maxSize }: ParseVideoPluginParams) => {
    const uploadVideoManager = new UploadVideoManager(
        uploadOptions,
        tokenRefreshManager,
        maxSize ?? DEFAULT_MAX_SIZE,
        UPLOAD_VIDEO_PLUGIN_KEY
    )

    let pendingUpload: { files: File[]; pos: number } | null = null

    return new Plugin<DecorationSet>({
        key: UPLOAD_VIDEO_PLUGIN_KEY,

        state: {
            init() {
                return DecorationSet.empty
            },
            apply(tr, set) {
                set = set.map(tr.mapping, tr.doc)

                const meta = tr.getMeta(UPLOAD_VIDEO_PLUGIN_KEY)

                if (Array.isArray(meta)) {
                    pendingUpload = { files: [...meta], pos: tr.selection.from }
                    return set
                }

                if (!isPlaceholderMeta(meta)) return set

                if (meta.type === 'addMany') {
                    meta.items.forEach(({ id, pos }) => {
                        const widget = createUploadPlaceholderWidget('视频上传中...')
                        const deco = Decoration.widget(pos, widget, { id, block: true })
                        set = set.add(tr.doc, [deco])
                    })
                } else if (meta.type === 'add') {
                    const widget = createUploadPlaceholderWidget('视频上传中...')
                    const deco = Decoration.widget(meta.pos, widget, { id: meta.id, block: true })
                    set = set.add(tr.doc, [deco])
                } else if (meta.type === 'remove') {
                    const match = set.find(undefined, undefined, (spec: { id?: string }) => spec.id === meta.id)
                    set = set.remove(match)
                }

                return set
            }
        },

        props: {
            decorations(state) {
                return this.getState(state)
            },
            handleDOMEvents: {
                paste(view, event) {
                    const items = [...(event.clipboardData?.items ?? [])].filter(item => /video/i.test(item.type))
                    const files = items.map(item => item.getAsFile()).filter(Boolean) as File[]
                    if (files.length > 0) {
                        event.preventDefault()
                        uploadVideoManager.createDecorationPlaceholdersAndUpload(view, files, view.state.selection.from)
                        return false
                    }

                    const html = event.clipboardData?.getData?.('text/html')
                    if (!html || !uploadOptions) return false

                    let slice: Slice | null = null
                    const parser = view.props.clipboardParser as
                        | ((view: EditorView, data: DataTransfer, event: ClipboardEvent) => Slice)
                        | undefined
                    if (typeof parser === 'function') {
                        slice = parser(view, event.clipboardData!, event)
                    } else if (typeof window !== 'undefined' && window.DOMParser) {
                        const doc = new window.DOMParser().parseFromString(html, 'text/html')
                        if (doc?.body) {
                            slice = PM_DOMParser.fromSchema(view.state.schema).parseSlice(doc.body)
                        }
                    }
                    if (!slice) return false

                    const { newSlice, slots } = processSliceRemoveVideos(slice)
                    if (slots.length === 0) return false

                    event.preventDefault()
                    const from = view.state.selection.from
                    uploadVideoManager.pasteHtmlWithDecorationPlaceholders(view, newSlice, slots, from)
                    return false
                },

                drop(view, event) {
                    const hasFiles = event.dataTransfer?.files && event.dataTransfer.files.length > 0
                    if (!hasFiles) return false

                    const files = [...event.dataTransfer.files].filter(file => /video/i.test(file.type))
                    if (files.length === 0) return false

                    const coordinates = view.posAtCoords({
                        left: event.clientX,
                        top: event.clientY
                    })
                    if (!coordinates || !uploadOptions) return false

                    event.preventDefault()
                    uploadVideoManager.createDecorationPlaceholdersAndUpload(view, files, coordinates.pos)
                    return false
                }
            }
        },

        view() {
            return {
                update(view) {
                    if (pendingUpload) {
                        const { files, pos } = pendingUpload
                        pendingUpload = null
                        queueMicrotask(() => {
                            uploadVideoManager.createDecorationPlaceholdersAndUpload(view, files, pos)
                        })
                    }
                }
            }
        }
    })
}
